import { useState, type ReactElement } from 'react';
import { Download, ServerCog, ShieldCheck } from 'lucide-react';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';

import { PASSOS_DO_ALGORITMO, PENDENCIA_DA_SEMENTE, SCRIPT_DE_VERIFICACAO } from './algoritmo';
import { ConferenciaDoServidor, HashCopiavel, SeloDeConferencia } from './componentes';
import { rotuloEvento, type DadosDeVerificacao, type SorteioPublico } from './contrato';
import { useDadosDeVerificacao, useVerificacaoDoServidor } from './dados';
import { verificarNoNavegador, type VerificacaoLocal } from './verificacao';

const FALHA = 'Não foi possível buscar os dados agora. Tente de novo em instantes.';

function mensagem(erro: unknown): string {
  return erro instanceof ErroApi && erro.message ? erro.message : FALHA;
}

/** Salva o JSON no computador de quem pediu, sem passar por outro servidor. */
function salvarJson(dados: DadosDeVerificacao): void {
  const blob = new Blob([JSON.stringify(dados, null, 2)], { type: 'application/json' });
  const endereco = URL.createObjectURL(blob);
  const ancora = document.createElement('a');
  ancora.href = endereco;
  ancora.download = `sorteio-${dados.sorteioId}-dados-verificacao.json`;
  document.body.appendChild(ancora);
  ancora.click();
  ancora.remove();
  URL.revokeObjectURL(endereco);
}

function Valor({ rotulo, children }: { rotulo: string; children: ReactElement }): ReactElement {
  return (
    <div className="min-w-0 space-y-0.5">
      <dt className="text-muted-foreground text-xs uppercase tracking-wide">{rotulo}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

/**
 * "Verifique você mesmo": o algoritmo em palavras, os hashes, a trilha e a
 * conferência feita NO NAVEGADOR (Web Crypto, `verificacao.ts`), sem confiar no
 * servidor. A verificação que o servidor faz aparece à parte, com título
 * próprio — quem lê precisa saber qual é qual.
 */
export function VerifiqueVoceMesmo({ s }: { s: SorteioPublico }): ReactElement {
  const dados = useDadosDeVerificacao(s.id);
  const servidor = useVerificacaoDoServidor(s.id);
  const [local, setLocal] = useState<VerificacaoLocal | null>(null);
  const [verificando, setVerificando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function verificar() {
    setVerificando(true);
    setErro(null);
    setLocal(null);
    try {
      // Busca de novo a cada pedido: a conferência é sobre o que o servidor
      // entrega agora, não sobre o que ficou na memória da página.
      const r = await dados.refetch({ throwOnError: true });
      if (!r.data) throw new Error('sem dados');
      setLocal(await verificarNoNavegador(r.data));
    } catch (e) {
      setErro(mensagem(e));
    } finally {
      setVerificando(false);
    }
  }

  async function baixar() {
    setErro(null);
    try {
      const r = await dados.refetch({ throwOnError: true });
      if (r.data) salvarJson(r.data);
    } catch (e) {
      setErro(mensagem(e));
    }
  }

  return (
    <section aria-labelledby="titulo-verifique" className="space-y-4">
      <h2 id="titulo-verifique" className="text-xl font-bold tracking-tight">
        Verifique você mesmo
      </h2>
      <p className="text-muted-foreground text-sm leading-relaxed">
        O sorteio segue o algoritmo <span className="font-mono">{s.algoritmo}</span>. Com a semente
        revelada e a lista de participantes, qualquer pessoa refaz o cálculo e chega ao mesmo
        resultado.
      </p>

      <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed">
        {PASSOS_DO_ALGORITMO.map((p) => (
          <li key={p.titulo}>
            <span className="font-medium">{p.titulo}.</span> {p.texto}
          </li>
        ))}
      </ol>

      <div role="note" className="bg-muted/40 rounded-lg border p-4 text-sm leading-relaxed">
        <span className="font-medium">Sobre a semente.</span> {PENDENCIA_DA_SEMENTE}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Valores publicados</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-3">
            <Valor rotulo="Hash do universo (lista de participantes)">
              <HashCopiavel valor={s.universoHash} rotulo="hash do universo" />
            </Valor>
            <Valor rotulo="Hash da semente (compromisso do preparo)">
              <HashCopiavel valor={s.sementeHash} rotulo="hash da semente" />
            </Valor>
            <Valor rotulo="Semente revelada">
              {s.sementeRevelada ? (
                <HashCopiavel valor={s.sementeRevelada} rotulo="semente revelada" />
              ) : (
                <span className="text-sm">Revelada na execução do sorteio.</span>
              )}
            </Valor>
            <Valor rotulo="Hash do resultado">
              {s.resultadoHash ? (
                <HashCopiavel valor={s.resultadoHash} rotulo="hash do resultado" />
              ) : (
                <span className="text-sm">Calculado na execução do sorteio.</span>
              )}
            </Valor>
            <Valor rotulo="Último hash da trilha">
              <HashCopiavel valor={s.trilhaUltimoHash} rotulo="último hash da trilha" />
            </Valor>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            Conferência no seu navegador
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground text-sm leading-relaxed">
            O navegador baixa os dados de verificação e refaz as contas aqui, sem pedir ao sistema
            que confirme nada. Quem preferir pode baixar os mesmos dados e conferir com o script{' '}
            <span className="font-mono">{SCRIPT_DE_VERIFICACAO}</span>, do repositório do sistema
            (só Node, sem dependências).
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button onClick={() => void verificar()} disabled={verificando}>
              <ShieldCheck className="mr-2 h-4 w-4" aria-hidden="true" />
              {verificando ? 'Verificando…' : 'Verificar no meu navegador'}
            </Button>
            <Button variant="outline" onClick={() => void baixar()} disabled={dados.isFetching}>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Baixar dados de verificação (JSON)
            </Button>
          </div>

          {erro && (
            <Alert variant="destructive">
              <AlertDescription>{erro}</AlertDescription>
            </Alert>
          )}

          <div aria-live="polite">
            {local && (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  {local.confere
                    ? 'Seu navegador refez as contas e tudo confere.'
                    : 'Seu navegador encontrou divergência. Guarde os dados baixados e registre um pedido de esclarecimento à SMT.'}
                </p>
                <ul aria-label="Conferências feitas no navegador">
                  {local.checagens.map((c) => (
                    <li key={c.id} className="space-y-0.5 border-b py-2 last:border-b-0">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <span className="text-sm font-medium">{c.titulo}</span>
                        <SeloDeConferencia resultado={c.resultado} />
                      </div>
                      <p className="text-muted-foreground text-xs">{c.detalhe}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {dados.data && dados.data.trilha.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Trilha de eventos</h3>
              <ol className="space-y-2">
                {[...dados.data.trilha]
                  .sort((a, b) => a.seq - b.seq)
                  .map((e) => (
                    <li key={e.seq} className="border-l-2 pl-3 text-sm">
                      <span className="font-medium">
                        {e.seq}. {rotuloEvento(e.tipo)}
                      </span>
                      <span className="text-muted-foreground flex flex-wrap items-center gap-x-2 text-xs">
                        anterior{' '}
                        <HashCopiavel
                          valor={e.hashAnterior}
                          rotulo={`hash anterior do evento ${e.seq}`}
                          abreviado
                        />
                      </span>
                      <span className="text-muted-foreground flex flex-wrap items-center gap-x-2 text-xs">
                        hash{' '}
                        <HashCopiavel valor={e.hash} rotulo={`hash do evento ${e.seq}`} abreviado />
                      </span>
                    </li>
                  ))}
              </ol>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ServerCog className="h-4 w-4" aria-hidden="true" />
            Verificação feita pelo servidor
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-muted-foreground text-sm leading-relaxed">
            Esta é a conferência que o próprio sistema faz. Ela não substitui a de cima: a do seu
            navegador não depende do que o servidor diz.
          </p>
          <Button
            variant="outline"
            onClick={() => void servidor.refetch()}
            disabled={servidor.isFetching}
          >
            {servidor.isFetching ? 'Pedindo…' : 'Pedir a verificação ao servidor'}
          </Button>
          {servidor.isError && (
            <Alert variant="destructive">
              <AlertDescription>{mensagem(servidor.error)}</AlertDescription>
            </Alert>
          )}
          {servidor.data && <ConferenciaDoServidor v={servidor.data} />}
        </CardContent>
      </Card>
    </section>
  );
}
